import RoleFormComponent from '../components/role-form.component';
import { RoleUpdate } from '../types/role.types';
import { useRoleGet, useRoleUpdate } from '../hooks/use-role.hook';
import { message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  extractCreateError,
  extractValidationError,
} from '../../../../../services/extract-api-error.service';
import { useEffect, useState, useCallback } from 'react';
import HeaderRow from '../../../../common/HeaderRow';
import { ValidationError } from '../../../../../types/api-response.types';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { DefaultValues } from 'react-hook-form';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import PageTitle from '@components/common/PageTitle';

interface FormSubmitHandler {
  (data: RoleUpdate, resetForm: () => void): void;
}

const RoleUpdateContainer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [formDefaultValues, setFormDefaultValues] = useState<DefaultValues<RoleUpdate>>({});
  const { data, isLoading, error } = useRoleGet(id ?? '');
  const { mutateAsync: updateRole, isPending } = useRoleUpdate();

  const extractFormDefaultValues = useCallback(async () => {
    if (!data?.data) return;
    const { data: roleData } = data;

    setFormDefaultValues({
      name: roleData.name ?? undefined,
      permissions: roleData.permissions ?? undefined,
    });
  }, [data]);

  useEffect(() => {
    let isMounted = true; // to prevent memory leaks
    extractFormDefaultValues();
    return () => {
      isMounted = false;
    };
  }, [extractFormDefaultValues]);

  const onFormSubmit: FormSubmitHandler = useCallback(
    async (data, resetForm) => {
      if (!id) return;
      // Reset validation errors on new submission
      setValidationErrors([]);
      try {
        await updateRole({ id, data });
        message.success(t('crud.update.success', { entity: t('roles.title') }));
        navigate(-1);
      } catch (error: any) {
        const errorFields = extractValidationError(error);
        setValidationErrors(errorFields);
        const errorMessage = extractCreateError(
          error,
          t('crud.update.error', { entity: t('roles.title') })
        );
        message.error(errorMessage);
      }
    },
    [id, updateRole, t, navigate]
  );

  if (!id) {
    return (
      <ErrorFallback
        details={t('common.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );
  }

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.update.title', { entity: t('roles.title') })} />
        <HeaderRow
          title={t('crud.update.title', { entity: t('roles.title') })}
          showBackButton={true}
        />
        <ErrorFallback
          details={t('common.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('crud.update.title', { entity: t('roles.title') })} />
      <HeaderRow
        title={t('crud.update.title', { entity: t('roles.title') })}
        showBackButton={true}
      />
      <div className="row mt-3">
        <RoleFormComponent
          onFormSubmit={onFormSubmit}
          isLoading={isPending || isLoading}
          defaultValues={formDefaultValues}
          validationErrors={validationErrors}
        />
      </div>
    </>
  );
};

export default WithPermissionCheck(RoleUpdateContainer, 'role.update');
